/* eslint-disable no-undef */
// @flow
import { useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { prepareCurrentNetworkStatus } from '@alphaott/smart-tv-common/src/hooks/useNetworkStatus'
import { setDeviceInfo } from '@alphaott/smart-tv-current-device/actions'

export const useInitDeviceInfo = () => {
  const dispatch = useDispatch()

  // eslint-disable-next-line complexity
  const setDeviceSystemInfo = useCallback(async () => {
    // eslint-disable-next-line max-len
    // https://developer.samsung.com/smarttv/develop/api-references/tizen-web-device-api-references/systeminfo-api/getting-device-capabilities-using-systeminfo-api.html
    const brand = tizen?.systeminfo?.getCapability?.('http://tizen.org/system/manufacturer')
    const model = tizen?.systeminfo?.getCapability?.('http://tizen.org/system/model_name')
    const id = tizen?.systeminfo?.getCapability?.('http://tizen.org/system/tizenid')
    const os = tizen?.systeminfo?.getCapability?.('http://tizen.org/system/platform.name')
    const osVersion = tizen?.systeminfo?.getCapability?.(
      'http://tizen.org/feature/platform.version',
    )
    const isConnectedToGateway = webapis?.network?.isConnectedToGateway?.()

    dispatch(
      setDeviceInfo({
        _id: id,
        serial: id,
        brand,
        model,
        os,
        osVersion,
        networkStatus: prepareCurrentNetworkStatus(!!isConnectedToGateway || navigator.onLine),
        visibilityStatus: document.hidden ? 'hidden' : 'visible',
      }),
    )
  }, [dispatch])

  const setWifiInfo = useCallback(() => {
    tizen?.systeminfo?.getPropertyValue?.('WIFI_NETWORK', (wifi) => {
      const isConnectedToGateway = webapis?.network?.isConnectedToGateway?.()

      dispatch(
        setDeviceInfo({
          networkStatus: prepareCurrentNetworkStatus(!!isConnectedToGateway || navigator.onLine),
          ...(wifi.status === 'ON' && {
            mac: wifi.macAddress,
            network: {
              mode: 'WIFI',
              localIpAddress: wifi.ipAddress,
              macAddress: wifi.macAddress,
            },
          }),
        }),
      )
    })
  }, [dispatch])

  const setEthernetInfo = useCallback(() => {
    tizen?.systeminfo?.getPropertyValue?.('ETHERNET_NETWORK', (ethernet) => {
      const isConnectedToGateway = webapis?.network?.isConnectedToGateway?.()

      dispatch(
        setDeviceInfo({
          networkStatus: prepareCurrentNetworkStatus(!!isConnectedToGateway || navigator.onLine),
          ...(ethernet.status === 'CONNECTED' && {
            mac: ethernet.macAddress,
            network: {
              mode: 'ETHERNET',
              localIpAddress: ethernet.ipAddress,
              macAddress: ethernet.macAddress,
            },
          }),
        }),
      )
    })
  }, [dispatch])

  const setVisibilityStatus = useCallback(() => {
    const currentVisibility = !document.hidden

    requestAnimationFrame(() => {
      const nextFrameVisibility = !document.hidden
      const isVisible = nextFrameVisibility ?? currentVisibility

      dispatch(
        setDeviceInfo({
          visibilityStatus: isVisible ? 'visible' : 'hidden',
        }),
      )
    })
  }, [dispatch])

  useEffect(() => {
    setDeviceSystemInfo()
    setWifiInfo()
    setEthernetInfo()
    const wifiListenerId = tizen?.systeminfo?.addPropertyValueChangeListener?.(
      'WIFI_NETWORK',
      setWifiInfo,
    )
    const ethernetListenerId = tizen?.systeminfo?.addPropertyValueChangeListener?.(
      'ETHERNET_NETWORK',
      setEthernetInfo,
    )
    document.addEventListener('visibilitychange', setVisibilityStatus)
    return () => {
      tizen?.systeminfo?.removePropertyValueChangeListener?.(wifiListenerId)
      tizen?.systeminfo?.removePropertyValueChangeListener?.(ethernetListenerId)
      // webapis.network.removeNetworkStateChangeListener(networkStateListenerId)
      document.removeEventListener('visibilitychange', setVisibilityStatus)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
